import type { NavigatePort } from '@heidi/runtime/shell';
import { ArrowUpRight } from 'lucide-react';
import type { ReactNode } from 'react';

import { WORK_CHAT_HOME_PATH } from '../../../contracts/projects/chat-home-path';
import { unclobberedAttr } from '../../../contracts/projects/ref-tags';

const PROJECT_ID_RE = /^[a-f0-9]{24}$/i;

const resolveProjectId = (raw: string | undefined): string => {
  const unclobbered = unclobberedAttr(raw);
  return PROJECT_ID_RE.test(unclobbered) ? unclobbered : '';
};

type ChatCiteChipProps = {
  children?: ReactNode;
  /** Canonical ``<project data-id>`` (HTML kebab). */
  'data-id'?: string;
  /** Same attr after hast/rehype camelCase. */
  dataId?: string;
  /** Legacy ``<chat data-project-id>``. */
  'data-project-id'?: string;
  dataProjectId?: string;
  /** Legacy ``<chat id>`` (may be ``user-content-<hex>`` after sanitize). */
  id?: string;
  /**
   * Leaves for the chat this chip names. The IMPERATIVE half of the navigate
   * port: the chip resolves its own destination out of markdown attributes the
   * app never sees, so the app cannot name the href and `InAppHref` would have
   * nothing to carry — the chip names it and the composition point pushes it.
   *
   * Required, with no default. An unsupplied navigate has no honest no-op: a
   * chip that renders and silently does nothing on click is worse than one that
   * does not compile.
   */
  onNavigate: NavigatePort;
};

/**
 * Inline Projects citation chip from assistant prose:
 * ``<project data-id="<project_id}">Title</project>``.
 *
 * ``data-id`` must be a Projects ``project_id`` (24-char ObjectId). An
 * ``agent_run_id`` (often 32-char hex) fails validation and degrades to
 * plain children — that is intentional so we never navigate to a bogus route.
 */
export const ChatCiteChip = ({
  id,
  'data-id': dataIdAttr,
  dataId: dataIdCamel,
  'data-project-id': dataProjectIdAttr,
  dataProjectId: dataProjectIdCamel,
  children,
  onNavigate,
}: ChatCiteChipProps) => {
  const projectId =
    resolveProjectId(dataIdAttr) ||
    resolveProjectId(dataIdCamel) ||
    resolveProjectId(dataProjectIdAttr) ||
    resolveProjectId(dataProjectIdCamel) ||
    resolveProjectId(id);
  if (!projectId) {
    return <>{children}</>;
  }

  return (
    <button
      type="button"
      data-testid="chat-cite-chip"
      className="mx-0.5 inline-flex max-w-full items-center gap-0.5 rounded-md border border-transparent bg-background-secondary px-1.5 py-px align-middle text-[11px] leading-4 text-text-primary transition-colors hover:bg-background-secondary-hover"
      onClick={() => {
        void onNavigate(`${WORK_CHAT_HOME_PATH}/${projectId}`);
      }}
    >
      <ArrowUpRight className="size-3 shrink-0 text-text-tertiary" aria-hidden />
      <span className="max-w-56 truncate">{children}</span>
    </button>
  );
};

export type { ChatCiteChipProps };
